// @file Helper for whiteboard upload
import { isAppCapableOf } from '@@/bits/app_can'
import device from '@@/bits/device'
import { readImageFileData } from '@@/bits/file_handler'
import { isAppUsing } from '@@/bits/flip'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { CANVAS_HEIGHT, CANVAS_WIDTH, getCanvasDimensions } from '@@/bits/whiteboard'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { TLShapeId } from 'tldraw'

function throwErrorForMissingDataInNewestPost(): void {
  throw new Error('Missing data in newest post')
}

const DEFAULT_ASPECT_RATIO = 11 / 6
const DEFAULT_WIDTH = 220
const MAXIMUM_NUMBER_OF_FILES = 12
const DEFAULT_POSITION = {
  x: CANVAS_WIDTH / 2 - CANVAS_WIDTH / 3 / 2,
  y: CANVAS_HEIGHT / 2 - CANVAS_HEIGHT / 3 / 2,
}

async function calculateFileDimensions(file: File): Promise<{ width: number; height: number; aspectRatio: number }> {
  let width = DEFAULT_WIDTH
  let height = DEFAULT_WIDTH / DEFAULT_ASPECT_RATIO
  let aspectRatio = DEFAULT_ASPECT_RATIO

  if (file.type.startsWith('image/')) {
    const imageFileData = await readImageFileData(file)
    if (imageFileData != null) {
      aspectRatio = imageFileData.width / imageFileData.height
      width = DEFAULT_WIDTH
      height = width / aspectRatio
    }
  }
  return { width, height, aspectRatio }
}

async function calculateUploadedFilePositionsAndDimensions({
  files,
  dropPosition = DEFAULT_POSITION,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
}: {
  files: File[]
  dropPosition?: { x: number; y: number }
  canvasWidth?: number
  canvasHeight?: number
}): Promise<Array<{ file: File; width: number; height: number; aspectRatio: number; x: number; y: number }>> {
  const spaceBetweenShapesInStack = 12
  // Determine stacking direction based on drop position
  // Files should be laid out inwards from the drop position
  const xDirection = dropPosition.x < canvasWidth / 2 ? 1 : -1
  const yDirection = dropPosition.y < canvasHeight / 2 ? 1 : -1

  // Calculate positions and dimensions for all files
  return await Promise.all(
    files.map(async (file, index) => {
      const { width, height, aspectRatio } = await calculateFileDimensions(file)

      // Calculate position for each shape
      const x = dropPosition.x + (xDirection === -1 ? -width : 0) + xDirection * index * spaceBetweenShapesInStack
      const y = dropPosition.y + (yDirection === -1 ? -height : 0) + yDirection * index * spaceBetweenShapesInStack

      return { file, width, height, aspectRatio, x, y }
    }),
  )
}

/**
 * Constrains the position of an PadletAttachmentShape within the canvas boundaries.
 * This function ensures that the shape remains visible on the canvas.
 *
 * @param {PadletAttachmentShape} shape - The shape to be constrained.
 * @param {Object} newDimensions - The new dimensions of the shape.
 * @param {number} [newDimensions.w] - The new width of the shape.
 * @param {number} [newDimensions.h] - The new height of the shape.
 * @returns {Object} An object containing the new x and y coordinates for the shape.
 * @property {number} newX - The new x-coordinate for the shape.
 * @property {number} newY - The new y-coordinate for the shape.
 */
function constrainShapePositionToCanvas(
  shape: PadletAttachmentShape,
  newDimensions: { w?: number; h?: number },
): { newX: number; newY: number } {
  // If the shape or the new dimensions are not set, return the current position
  if (shape.props.w == null || shape.props.h == null || newDimensions.w == null || newDimensions.h == null) {
    return { newX: shape.x, newY: shape.y }
  }

  const { width: canvasWidth, height: canvasHeight } = getCanvasDimensions()
  // Calculate new position to keep the shape within the viewport
  let newX = shape.x
  let newY = shape.y
  const currentWidth = shape.props.w
  const currentHeight = shape.props.h
  const newWidth: number = newDimensions.w
  const newHeight: number = newDimensions.h
  // Calculate current paddings
  const paddingLeft = shape.x
  const paddingTop = shape.y
  const paddingRight = canvasWidth - (shape.x + currentWidth)
  const paddingBottom = canvasHeight - (shape.y + currentHeight)

  // Adjust x-coordinate
  if (newX + newWidth > CANVAS_WIDTH) {
    // If expanding beyond right edge, maintain right padding
    newX = CANVAS_WIDTH - newWidth - paddingRight
  } else if (newX + newWidth < shape.x + currentWidth) {
    // If shrinking, maintain left padding
    newX = paddingLeft
  }

  // Adjust y-coordinate
  if (newY + newHeight > CANVAS_HEIGHT) {
    // If expanding beyond bottom edge, maintain bottom padding
    newY = CANVAS_HEIGHT - newHeight - paddingBottom
  } else if (newY + newHeight < shape.y + currentHeight) {
    // If shrinking, maintain top padding
    newY = paddingTop
  }
  return { newX, newY }
}

/**
 * Deletes failed uploads from the editor.
 *
 * This function performs two types of deletions:
 * 1. Deletes shapes that are not uploaded and visible to the current user.
 *    This covers the case where the same user opens another tab while a shape is uploading,
 *    ensuring it doesn't delete shapes they cannot see.
 * 2. Deletes shapes that are not uploaded and were created over 24 hours ago.
 *    This is a cleanup operation for the store in case the user never opens that tab again.
 */
function deleteFailedUploads(): void {
  const editor = getEditorObject()
  if (editor == null) return

  const failedUploads = editor.store
    .allRecords()
    .filter(
      (record) =>
        record.typeName === 'shape' && record?.type === 'padlet-attachment-shape' && record.meta.isUploaded === false,
    )

  const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000

  failedUploads.forEach((shape) => {
    const isViewableByUser = useWhiteboardPermissionsStore().canIViewShape(shape)
    const isOlderThanOneDay = typeof shape.meta.ca === 'number' && Date.now() - shape.meta.ca > ONE_DAY_IN_MS

    if (isViewableByUser || isOlderThanOneDay) {
      editor.deleteShape(shape.id as TLShapeId)
    }
  })
}

const shouldEnableWhiteboardInPlaceUploadingAnimation =
  (!device.app && isAppUsing('sandboxInPlaceUploadingAnimation')) ||
  isAppCapableOf('whiteboardInPlaceUploadingAnimation')

export {
  calculateUploadedFilePositionsAndDimensions,
  constrainShapePositionToCanvas,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_POSITION,
  DEFAULT_WIDTH,
  deleteFailedUploads,
  MAXIMUM_NUMBER_OF_FILES,
  shouldEnableWhiteboardInPlaceUploadingAnimation,
  throwErrorForMissingDataInNewestPost,
}
